import React, { useEffect, useRef, useState } from "react";
import { Modal, Button, Spinner, Form, Row, Col } from "react-bootstrap";
import { FaRegFileAlt } from "react-icons/fa";
import { Select } from "antd";
import toast from "react-hot-toast";
import {
  getFinalDocuments,
  previewFinalDocument,
  generateFinalDocuments,
} from "../../services/FinalDocumentServices";
import config from "../../config";

const BUTTON_SPINNER_STYLE = {
  width: "0.85rem",
  height: "0.85rem",
  borderWidth: "0.15em",
  verticalAlign: "-0.1em",
};

const BODY_SPINNER_STYLE = {
  width: "1.75rem",
  height: "1.75rem",
  borderWidth: "0.2em",
};

const PREVIEW_FRAME_STYLE = {
  width: "100%",
  height: "60vh",
  border: "1px solid #dee2e6",
  borderRadius: "6px",
  background: "#fff",
};

const buildEmployeeLabel = (emp) =>
  [emp?.first_name, emp?.middle_name, emp?.last_name]
    .filter(Boolean)
    .join(" ") + (emp?.employment_id ? ` - ${emp.employment_id}` : "");

const GenerateDocumentModal = ({
  show,
  handleClose,
  employeeData,
  fetchRequestedDocument,
}) => {
  const frameRef = useRef(null);
  const [employeeId, setEmployeeId] = useState(null);
  const [documentId, setDocumentId] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [fetchingDocuments, setFetchingDocuments] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewKey, setPreviewKey] = useState(0);
  const [fetchingPreview, setFetchingPreview] = useState(false);
  const [edited, setEdited] = useState(false);
  const [sendEmail, setSendEmail] = useState(false);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    setDocumentId(null);
    setDocuments([]);
    setPreview(null);
    if (!employeeId) return;

    const fetchDocuments = async () => {
      setFetchingDocuments(true);
      try {
        const response = await getFinalDocuments(employeeId);
        setDocuments(response?.data || []);
      } catch (error) {
        toast.error(
          error?.response?.data?.message || "Unable to load documents"
        );
      } finally {
        setFetchingDocuments(false);
      }
    };

    fetchDocuments();
  }, [employeeId]);

  const loadPreview = async () => {
    setFetchingPreview(true);
    setEdited(false);
    try {
      const response = await previewFinalDocument({
        employee_id: employeeId,
        document_id: documentId,
      });
      setPreview(response?.data || null);
      setPreviewKey((key) => key + 1);
      if (!response?.data?.personal_email) setSendEmail(false);
    } catch (error) {
      setPreview(null);
      toast.error(
        error?.response?.data?.message || "Unable to load document preview"
      );
    } finally {
      setFetchingPreview(false);
    }
  };

  useEffect(() => {
    setPreview(null);
    if (employeeId && documentId) loadPreview();
  }, [documentId]);

  const handleFrameLoad = () => {
    const frameDocument = frameRef.current?.contentDocument;
    if (!frameDocument) return;
    frameDocument.designMode = "on";
    frameDocument.addEventListener("input", () => setEdited(true));
  };

  const getEditedHtml = () => {
    const frameDocument = frameRef.current?.contentDocument;
    if (!frameDocument?.documentElement) return null;
    frameDocument.designMode = "off";
    const html = `<!DOCTYPE html>${frameDocument.documentElement.outerHTML}`;
    frameDocument.designMode = "on";
    return html;
  };

  const downloadDocument = async (file) => {
    const response = await fetch(`${config.IMAGE_URL}${file.file_url}`);
    if (!response.ok) throw new Error(`Unable to download ${file.document_name}`);

    const blob = await response.blob();
    const objectUrl = window.URL.createObjectURL(blob);
    const anchor = document.createElement("a");

    anchor.href = objectUrl;
    anchor.download = file.file_name;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    window.URL.revokeObjectURL(objectUrl);
  };

  const handleGenerate = async () => {
    if (!employeeId || !documentId || !preview) return;

    setGenerating(true);
    try {
      const html = edited ? getEditedHtml() : null;
      const response = await generateFinalDocuments({
        employee_id: employeeId,
        document_ids: [documentId],
        action: sendEmail ? "both" : "save",
        source: "documents",
        contents: html ? { [documentId]: html } : {},
      });

      toast.success(response?.message || "Document generated successfully");

      for (const file of response?.data?.documents || []) {
        try {
          await downloadDocument(file);
        } catch (error) {
          toast.error(error.message);
        }
      }

      fetchRequestedDocument?.();
      handleClose();
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Failed to generate document"
      );
    } finally {
      setGenerating(false);
    }
  };

  const personalEmail = preview?.personal_email;

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      backdrop="static"
      size="xl"
    >
      <Modal.Header>
        <h5 className="fw-bold mb-0">Generate Document</h5>
      </Modal.Header>

      <Modal.Body>
        <Row className="g-3">
          <Col md={6}>
            <label className="form-label fw-semibold mb-2">
              Select employee
            </label>
            <Select
              style={{ width: "100%" }}
              placeholder="Select employee"
              value={employeeId}
              onChange={(value) => setEmployeeId(value ?? null)}
              options={(employeeData || []).map((emp) => ({
                value: emp.id,
                label: buildEmployeeLabel(emp),
              }))}
              showSearch
              filterOption={(input, option) =>
                option?.label?.toLowerCase().includes(input.toLowerCase())
              }
              allowClear
              disabled={generating}
              getPopupContainer={(trigger) =>
                trigger.parentElement || document.body
              }
              dropdownStyle={{ zIndex: 9999 }}
            />
          </Col>
          <Col md={6}>
            <label className="form-label fw-semibold mb-2">
              Select document
            </label>
            <Select
              style={{ width: "100%" }}
              placeholder={
                employeeId ? "Select document" : "Select an employee first"
              }
              value={documentId}
              onChange={(value) => setDocumentId(value ?? null)}
              options={documents.map((item) => ({
                value: item.id,
                label: item.is_generated
                  ? `${item.document_name} (already generated)`
                  : item.document_name,
              }))}
              showSearch
              filterOption={(input, option) =>
                option?.label?.toLowerCase().includes(input.toLowerCase())
              }
              allowClear
              loading={fetchingDocuments}
              disabled={!employeeId || generating}
              notFoundContent={
                fetchingDocuments
                  ? "Loading..."
                  : "No documents are configured for this company."
              }
              getPopupContainer={(trigger) =>
                trigger.parentElement || document.body
              }
              dropdownStyle={{ zIndex: 9999 }}
            />
          </Col>
        </Row>

        <div className="mt-4">
          {fetchingPreview ? (
            <div className="d-flex flex-column align-items-center justify-content-center py-5">
              <Spinner
                animation="border"
                variant="primary"
                style={BODY_SPINNER_STYLE}
              />
              <div className="text-muted small mt-3">Loading preview...</div>
            </div>
          ) : preview ? (
            <>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <label className="form-label fw-semibold mb-0">
                  Preview{" "}
                  <span className="text-muted fw-normal small">
                    (click on the letter to edit)
                  </span>
                </label>
                {edited && (
                  <Button
                    variant="link"
                    size="sm"
                    className="p-0"
                    onClick={loadPreview}
                    disabled={generating}
                  >
                    Reset changes
                  </Button>
                )}
              </div>
              <iframe
                key={previewKey}
                ref={frameRef}
                title="Document preview"
                srcDoc={preview.html}
                sandbox="allow-same-origin"
                style={PREVIEW_FRAME_STYLE}
                onLoad={handleFrameLoad}
              />

              <Form.Check
                className="mt-3"
                type="checkbox"
                id="generate-document-send-email"
                checked={sendEmail}
                onChange={(e) => setSendEmail(e.target.checked)}
                disabled={!personalEmail || generating}
                label={
                  personalEmail ? (
                    <>
                      Send to personal email <strong>{personalEmail}</strong>
                    </>
                  ) : (
                    "Send to personal email (no personal email address on record)"
                  )
                }
              />
            </>
          ) : (
            <div className="text-center py-5">
              <FaRegFileAlt className="text-muted mb-2" size={28} />
              <div className="text-muted small">
                Select an employee and a document to see the preview.
              </div>
            </div>
          )}
        </div>
      </Modal.Body>

      <Modal.Footer className="border-top-0 pt-0">
        <Button
          variant="light"
          size="sm"
          onClick={handleClose}
          disabled={generating}
        >
          Close
        </Button>
        <Button
          variant="primary"
          size="sm"
          onClick={handleGenerate}
          disabled={!preview || fetchingPreview || generating}
        >
          {generating ? (
            <>
              <Spinner
                animation="border"
                className="me-2"
                style={BUTTON_SPINNER_STYLE}
              />
              Generating...
            </>
          ) : (
            "Generate"
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default GenerateDocumentModal;
